import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import ClaimFormSparteCostTypes from "./ClaimFormSparteCostTypes.jsx";
import ClaimFormSparteClaimCauses from "./ClaimFormSparteClaimCauses.jsx";
import ClaimFormSparteDialogHeading from "./ClaimFormSparteDialogHeading.jsx";

/**
 * Applies one cost type and/or cause setting to several Sparten of the form.
 * A section left at "Unverändert lassen" (undefined) is not sent, so each
 * Sparte keeps its own value there.
 */
export default function ClaimFormSparteBulkDialog({
  sparten,
  coverageTypes,
  definition,
  causes,
  isSaving,
  onSave,
  onClose,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [draft, setDraft] = useState({
    cost_types: undefined,
    claim_causes: undefined,
  });

  const selectedSparten = sparten.filter((sparte) =>
    selectedIds.includes(sparte.id)
  );
  const selectedCoverageTypes = [
    ...new Set(selectedSparten.map((sparte) => sparte.coverage_type ?? null)),
  ];
  const draftSparte = {
    name: "für Auswahl",
    coverage_type:
      selectedCoverageTypes.length === 1 ? selectedCoverageTypes[0] : null,
    ...draft,
  };

  // Sparten grouped by coverage type in the form's order; a form without
  // coverage types has one group without a heading.
  const groups = [
    ...coverageTypes.map(({ key, label }) => ({ key, label })),
    { key: null, label: coverageTypes.length ? "Ohne Deckungsart" : null },
  ]
    .map((group) => ({
      ...group,
      sparten: sparten.filter(
        (sparte) => (sparte.coverage_type ?? null) === group.key
      ),
    }))
    .filter((group) => group.sparten.length);

  // Keeps the selection in table order whatever order it was ticked in.
  const select = (ids, ticked) =>
    setSelectedIds((current) =>
      sparten
        .map((sparte) => sparte.id)
        .filter((id) => (ids.includes(id) ? ticked : current.includes(id)))
    );

  const toggleGroup = (group) => {
    const ids = group.sparten.map((sparte) => sparte.id);
    select(ids, !ids.every((id) => selectedIds.includes(id)));
  };

  const changes = Object.fromEntries(
    Object.entries(draft).filter(([, value]) => value !== undefined)
  );
  const ownCauseCount = draft.claim_causes
    ? causes.filter((cause) => draft.claim_causes.includes(cause.cause_id))
        .length
    : null;
  const isValid = draft.cost_types?.length !== 0 && ownCauseCount !== 0;
  const canSave =
    selectedIds.length > 0 &&
    Object.keys(changes).length > 0 &&
    isValid &&
    !isSaving;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Sammelbearbeitung</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Kostenarten und Ursachen für mehrere Sparten gleichzeitig festlegen.
            Bereiche auf &ldquo;Unverändert lassen&rdquo; werden nicht geändert.
          </Typography>
          <Box>
            <ClaimFormSparteDialogHeading
              actions={
                <>
                  <Typography variant="body2" color="text.secondary" mr={1}>
                    {selectedIds.length} von {sparten.length} ausgewählt
                  </Typography>
                  <Button
                    variant="neutral"
                    size="small"
                    aria-label="Alle Sparten"
                    disabled={isSaving}
                    onClick={() => setSelectedIds(sparten.map(({ id }) => id))}
                  >
                    Alle
                  </Button>
                  <Button
                    variant="neutral"
                    size="small"
                    aria-label="Keine Sparten"
                    disabled={isSaving}
                    onClick={() => setSelectedIds([])}
                  >
                    Keine
                  </Button>
                </>
              }
            >
              1. Sparten auswählen
            </ClaimFormSparteDialogHeading>
            <Box display="flex" flexDirection="column" gap={2}>
              {groups.map((group) => (
                <FormGroup
                  key={group.key ?? "none"}
                  role="group"
                  aria-label={group.label ?? "Sparten"}
                  sx={{ display: "block" }}
                >
                  {group.label && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {group.label}
                      </Typography>
                      <Button
                        variant="neutral"
                        size="small"
                        aria-label={`Alle ${group.label}`}
                        disabled={isSaving}
                        onClick={() => toggleGroup(group)}
                      >
                        {group.sparten.every((sparte) =>
                          selectedIds.includes(sparte.id)
                        )
                          ? "keine"
                          : "alle"}
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ columnWidth: 260 }}>
                    {group.sparten.map((sparte) => (
                      <FormControlLabel
                        key={sparte.id}
                        sx={{ display: "flex", breakInside: "avoid", mb: 0.5 }}
                        label={sparte.name}
                        slotProps={{ typography: { variant: "body2" } }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedIds.includes(sparte.id)}
                            disabled={isSaving}
                            onChange={(event) =>
                              select([sparte.id], event.target.checked)
                            }
                          />
                        }
                      />
                    ))}
                  </Box>
                </FormGroup>
              ))}
            </Box>
          </Box>
          <Divider />
          <Box>
            <ClaimFormSparteDialogHeading>
              2. Einstellungen
            </ClaimFormSparteDialogHeading>
            <Box display="flex" flexDirection="column" gap={2.5}>
              <Box>
                <Typography variant="subtitle2">Kostenarten</Typography>
                <Typography variant="body2" color="text.secondary">
                  Welche Kostenpositionen sollen für diese Sparten angelegt
                  werden können?
                </Typography>
                <ClaimFormSparteCostTypes
                  sparte={draftSparte}
                  definition={definition}
                  disabled={isSaving}
                  allowUnchanged
                  mixedCoverage={selectedCoverageTypes.length > 1}
                  onChange={(cost_types) =>
                    setDraft((current) => ({ ...current, cost_types }))
                  }
                />
                {draft.cost_types?.length === 0 && (
                  <Typography variant="caption" color="error">
                    Mindestens eine Kostenart auswählen
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2">Ursachen</Typography>
                <Typography variant="body2" color="text.secondary">
                  Welche Ursachen sollen bei Schäden dieser Sparten auswählbar
                  sein?
                </Typography>
                <ClaimFormSparteClaimCauses
                  sparte={draftSparte}
                  causes={causes}
                  disabled={isSaving}
                  allowUnchanged
                  onChange={(claim_causes) =>
                    setDraft((current) => ({ ...current, claim_causes }))
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="tertiary" onClick={onClose} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={() => onSave(selectedIds, changes)}
          disabled={!canSave}
        >
          {selectedIds.length === 1
            ? "Auf 1 Sparte anwenden"
            : `Auf ${selectedIds.length} Sparten anwenden`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ClaimFormSparteBulkDialog.propTypes = {
  sparten: PropTypes.array.isRequired,
  coverageTypes: PropTypes.array.isRequired,
  definition: PropTypes.object,
  causes: PropTypes.array.isRequired,
  isSaving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
